import { Fragment, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import attach from '~/assets/icons/attach.svg';
import media from '~/assets/icons/media.svg';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { Divider } from '~/components/ui/Divider';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { SearchInput } from '~/components/ui/SearchInput';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { TextInput } from '~/components/ui/TextInput';
import { rage, useInputAction } from '~/rage';
import { InputAction } from '~/shared';
import { ModalAddPhoto } from '../../components/ModalAddPhoto';
import { PhotoItem } from '../../components/PhotoItem';
import {
	StyledContainer,
	StyledHead,
	StyledLinkInputContainer,
	StyledMediaHead,
	StyledPhotosContainer,
	StyledTextArea,
	StyledTextAreaContainer,
	StyledTextAreaStatsContainer,
} from './Feedback.styled';

const Feedback: React.FC = () => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [textareaValue, setTextareaValue] = useState('');
	const [themeValue, setThemeValue] = useState('');
	const [mediaLink, setMediaLink] = useState('');
	const [photos, setPhotos] = useState<string[]>(new Array(5).fill(''));
	const [activePhotoIndex, setActivePhotoIndex] = useState<number>(0);
	const [modalAddPhotoOpen, setModalAddPhotoOpen] = useState<boolean>(false);
	const [isSending, setIsSending] = useState(false);

	const config = rage.gameConfig.configs.gameplay;
	const messageMax = config?.feedbackMessageMaxLength ?? 1000;

	const handleClose = () => {
		rage.feedback.close();
	};

	useInputAction(InputAction.CommonMenuClose, handleClose);

	const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		if (e.target.value.length > messageMax) {
			return;
		}
		setTextareaValue(e.target.value);
	};

	// const buildImagesPayload = () => {
	// 	const values = [mediaLink, ...photos];

	// 	return values
	// 		.map((raw) => {
	// 			const value = raw.trim();
	// 			if (!value) return null;

	// 			if (value.startsWith('data:image')) {
	// 				const base64Index = value.indexOf('base64,');
	// 				const image =
	// 					base64Index !== -1
	// 						? value.substring(base64Index + 'base64,'.length)
	// 						: value;

	// 				return {
	// 					type: 'base64' as const,
	// 					image,
	// 				};
	// 			}

	// 			return {
	// 				type: 'url' as const,
	// 				image: value,
	// 			};
	// 		})
	// 		.filter(
	// 			(item): item is { type: 'url' | 'base64'; image: string } => !!item,
	// 		);
	// };

	const handleSubmit = async () => {
		if (!themeValue.trim() || isSending) {
			return;
		}

		setIsSending(true);
		try {
			// const payload = {
			// 	subject: themeValue.trim(),
			// 	message: textareaValue.trim(),
			// 	images: buildImagesPayload(),
			// };

			await rage.feedback.sendFeedback(themeValue.trim(), textareaValue.trim());
		} catch (error) {
			console.error(error);
		} finally {
			setIsSending(false);
		}
	};

	return (
		<Fragment>
			<StyledContainer>
				<StyledHead>
					<Text>Окно тестирования</Text>
					<CloseButton
						height={ButtonHeight.Height20}
						color={ButtonColors.Default}
						borderRadius={ButtonBorderRadius.Radius5}
						iconSize={10}
						onClick={handleClose}
					/>
				</StyledHead>
				<Divider $direction="H" style={{ margin: '20px 0', opacity: 0.1 }} />
				<TextInput
					placeholder="Введите название системы / темы сообщения"
					height={50}
					fontSize={14}
					padding="0 20px"
					value={themeValue}
					onChange={(e) => setThemeValue(e.target.value)}
				/>
				<StyledTextAreaContainer>
					<ScrollContainer
						height={163}
						width={330}
						style={{ position: 'relative' }}
						scrollbarRight={-10}
						scrollableRef={textareaRef}
						scrollbarThumbColor="rgba(255, 255, 255, 0.25)"
						scrollbarThumbHoverColor="#ffffff"
					>
						<StyledTextArea
							ref={textareaRef}
							placeholder="Введите ваше сообщение"
							value={textareaValue}
							onChange={handleTextareaChange}
						/>
					</ScrollContainer>
					<StyledTextAreaStatsContainer>
						<Text style={{ fontSize: 12, color: '#FFFFFF', opacity: 0.25 }}>
							Максимальное кол-во символов
						</Text>
						<Text style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.25)' }}>
							<span style={{ color: '#FFFFFF', fontWeight: 500 }}>
								{textareaValue.length}
							</span>{' '}
							/ {messageMax}
						</Text>
					</StyledTextAreaStatsContainer>
				</StyledTextAreaContainer>
				<StyledMediaHead>
					<SectionHeading text="Видео / фото контент" icon={media} />
					<Text style={{ fontSize: 16, color: '#ffffff', opacity: 0.25 }}>
						(Не обязательно)
					</Text>
				</StyledMediaHead>
				<StyledLinkInputContainer>
					<SearchInput
						placeholder="Ссылка на фото контентент"
						style={{ padding: '0 54px 0 20px' }}
						iconStyle={{
							right: '20px',
							fontSize: 14,
						}}
						icon={attach}
						value={mediaLink}
						onChange={(e) => setMediaLink(e.target.value)}
					/>
				</StyledLinkInputContainer>
				<StyledPhotosContainer>
					{photos.map((photo, index) => (
						<PhotoItem
							key={index}
							photo={photo}
							onClick={() => {
								setActivePhotoIndex(index);
								setModalAddPhotoOpen(true);
							}}
						/>
					))}
				</StyledPhotosContainer>
				<Button
					color={ButtonColors.Select2}
					height={ButtonHeight.Height50}
					style={{ marginTop: 30, fontSize: 16 }}
					disabled={!themeValue.trim() || isSending}
					onClick={handleSubmit}
				>
					<Text>
						{themeValue.trim()
							? 'Отправить сообщение'
							: 'Введите тему сообщения выше'}
					</Text>
				</Button>
			</StyledContainer>
			<CSSTransition
				in={modalAddPhotoOpen}
				timeout={300}
				classNames="modal"
				unmountOnExit
			>
				<ModalAddPhoto
					photos={photos}
					activePhotoIndex={activePhotoIndex}
					setPhotos={setPhotos}
					onClose={() => setModalAddPhotoOpen(false)}
				/>
			</CSSTransition>
		</Fragment>
	);
};

export { Feedback };
